import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import {
  Purchase,
  formatCurrency,
  getPurchaseStatusColor,
  getPaymentMethodDisplay,
} from '../../api/purchases';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface ViewPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchase: Purchase | null;
  onEdit: () => void;
  onDelete: () => void;
}

const ViewPurchaseModal: React.FC<ViewPurchaseModalProps> = ({
  isOpen,
  onClose,
  purchase,
  onEdit,
  onDelete,
}) => {
  if (!purchase) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title={`Purchase Order Details - ${purchase.invoiceNumber}`} onClose={onClose} />
      <BrandModalBody>
            <div className="mb-4 flex items-center justify-end space-x-2">
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-cyan-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-red-600 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </button>
            </div>

            {/* Purchase Status Badge */}
            <div className="mb-6">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getPurchaseStatusColor(purchase.status)}`}>
                {purchase.status}
              </span>
            </div>

            {/* Purchase Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <h4 className="text-md font-semibold text-slate-900 border-b border-gray-200 pb-2">
                  Purchase Information
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Purchase Order Number</dt>
                    <dd className="text-sm text-slate-900">{purchase.invoiceNumber}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Purchase Date</dt>
                    <dd className="text-sm text-slate-900">
                      {format(new Date(purchase.purchaseDate), 'EEEE, MMMM dd, yyyy')}
                    </dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Payment Method</dt>
                    <dd className="text-sm text-slate-900">
                      {getPaymentMethodDisplay(purchase.paymentMethod)}
                    </dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Total Amount</dt>
                    <dd className="text-xl font-bold text-slate-900">
                      {formatCurrency(purchase.total)}
                    </dd>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-md font-semibold text-slate-900 border-b border-gray-200 pb-2">
                  Supplier Information
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Supplier Name</dt>
                    <dd className="text-sm text-slate-900">{purchase.supplierName}</dd>
                  </div>
                  
                  {purchase.supplierPhone && (
                    <div>
                      <dt className="text-sm font-medium text-slate-500">Phone</dt>
                      <dd className="text-sm text-slate-900">{purchase.supplierPhone}</dd>
                    </div>
                  )}
                  
                  {purchase.supplierAddress && (
                    <div>
                      <dt className="text-sm font-medium text-slate-500">Address</dt>
                      <dd className="text-sm text-slate-900 whitespace-pre-line">
                        {purchase.supplierAddress}
                      </dd>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Purchase Items */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-slate-900 border-b border-gray-200 pb-2 mb-4">
                Purchase Items ({purchase.items.length})
              </h4>
              
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Unit Price
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {purchase.items.map((item, index) => (
                      <tr key={item.id || index} className="hover:bg-slate-50/80">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-slate-900">
                            {item.productName}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-slate-900">
                          {item.quantity}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-slate-900">
                          {formatCurrency(item.unitPrice)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-slate-900">
                          {formatCurrency(item.totalPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Total Summary */}
              <div className="mt-4 flex justify-end">
                <div className="bg-slate-50/80 px-4 py-3 rounded-lg">
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Total Amount</div>
                    <div className="text-xl font-bold text-slate-900">
                      {formatCurrency(purchase.total)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {purchase.notes && (
              <div className="mb-6">
                <h4 className="text-md font-semibold text-slate-900 border-b border-gray-200 pb-2 mb-4">
                  Notes
                </h4>
                <div className="bg-slate-50/80 rounded-lg p-4">
                  <p className="text-sm text-slate-700 whitespace-pre-line">
                    {purchase.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="border-t border-gray-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Created
                  </dt>
                  <dd className="text-sm text-slate-900">
                    {format(new Date(purchase.createdAt), 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Last Updated
                  </dt>
                  <dd className="text-sm text-slate-900">
                    {format(new Date(purchase.updatedAt), 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </div>
            </div>
      </BrandModalBody>

      <BrandModalFooter>
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
          Close
        </button>
        <button type="button" onClick={onEdit} className="btn btn-primary text-sm">
          Edit Purchase
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
};

export default ViewPurchaseModal; 